import { useQuery } from '@tanstack/react-query';
import { UserPaymentDetailsQueryKeys } from '../../queryKeys';
import {
  UseUserPaymentDetailsByIdProps,
  UseUserPaymentDetailsByIdQueryKeyProps,
} from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchUserPaymentDetailsById } from './service';
import { Nullable } from '@starsoft/common/interfaces';
import { useEffect } from 'react';
import { GenericError, UserPaymentDetails } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch user payment details by ID
 *
 * This hook handles fetching payment details for a specific user by their ID.
 * It uses React Query for data fetching, caching, and state management.
 * The hook also supports executing a callback function when data is successfully fetched.
 *
 * @param {UseUserPaymentDetailsByIdProps} options - Optional configuration object
 * @param {string} options.id - The ID of the payment details to fetch
 * @param {Function} options.externalSuccess - Optional callback function to execute when data is fetched successfully
 *
 * @returns {Object} An object containing:
 *   - data: The fetched payment details or null
 *   - isLoading: Boolean indicating if fetch is in progress (excluding idle state)
 *   - isFetching: Boolean indicating if a fetch is in progress
 *   - isError: Boolean indicating if an error occurred
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually trigger a refetch
 */
export function useUserPaymentDetailsById(
  options?: UseUserPaymentDetailsByIdProps,
) {
  const { id, externalSuccess } = options ?? {};
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const queryKey: UseUserPaymentDetailsByIdQueryKeyProps = [
    UserPaymentDetailsQueryKeys.ById,
    { id },
  ];

  const { data, isPending, isError, error, refetch, isFetching, fetchStatus } =
    useQuery<
      Nullable<UserPaymentDetails>,
      AxiosError<GenericError>,
      Nullable<UserPaymentDetails>,
      UseUserPaymentDetailsByIdQueryKeyProps
    >({
      queryKey,
      queryFn: queryContext =>
        fetchUserPaymentDetailsById(queryContext, axiosInstance),
      enabled: !!id,
      staleTime: 5 * 1000,
    });

  /**
   * Handles calling the external success callback when data is available
   * Only executes if both data and callback are present
   */
  function handleExternalSuccess() {
    if (!data || !externalSuccess) {
      return;
    }

    externalSuccess?.(data);
  }

  //eslint-disable-next-line
  useEffect(handleExternalSuccess, [data]);

  return {
    data,
    isLoading: isPending && fetchStatus !== 'idle',
    isFetching,
    isError,
    error,
    refetch,
  };
}
